import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { fetchMe, loginUser, registerUser, logoutUser } from "@/lib/api";
import { COMPROBANDO, DENTRO, FRENO, FUERA, INTENTOS, SIN_RESPUESTA, loQueDice, segundosDeFreno }
  from "@/lib/sesion.mjs";

const AuthContext = createContext(null);

/**
 * UN FALLO DE RED NO ES UN CIERRE DE SESIÓN, y hasta el 24 de septiembre de 2026 aquí lo era.
 *
 * QUÉ PASÓ. Gabri estaba compartiendo pantalla desde un Chromebook, enseñando las lecciones del
 * Cielo a la clase, y **le apareció la pantalla de acceso delante de los alumnos**. Su sesión
 * estaba perfectamente: la cookie dura una semana y no había caducado. Lo que pasó es que al
 * cargar la página esta comprobación —«¿quién soy?»— no obtuvo respuesta, y el `catch` de aquí
 * trataba *cualquier* fallo como «no hay nadie»: un 500, un tiempo agotado, un parpadeo del wifi
 * del instituto o el servidor gratuito despertando de su siesta salían por el mismo sitio que un
 * 401 de verdad. Una petición fallida y a la calle.
 *
 * LAS TRES SALIDAS, que antes eran dos y por eso se confundían:
 *
 *   · **fuera**  — el servidor CONTESTA que no hay sesión (401), o que esta cuenta no puede
 *                  pasar (403). Eso sí es estar fuera, y se va a la pantalla de acceso.
 *   · **dentro** — contesta quién eres.
 *   · **sin respuesta** — no contesta: red, 502, tiempo agotado. **No se sabe**, así que no se
 *                  decide: se reintenta cuatro veces en unos diez segundos —que es más de lo que
 *                  tarda en despertar el servidor— y, si sigue sin contestar, se dice lo que
 *                  pasa y se ofrece reintentar. Lo que NUNCA se hace es pedirle la contraseña a
 *                  alguien que ya la había puesto.
 *
 * Y NADA DE ESTO TOCA LA SEGURIDAD: quien decide si hay sesión sigue siendo el servidor, en cada
 * petición, con la cookie firmada. Esto sólo deja de sacar conclusiones de un silencio.
 */
const espera = (ms) => new Promise((r) => setTimeout(r, ms));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = comprobando, sin respuesta, o fuera; ver `sesion`
  const [ready, setReady] = useState(false);
  /** «comprobando» | «dentro» | «fuera» | «sin-respuesta». */
  const [sesion, setSesion] = useState(COMPROBANDO);
  /** Lo que el servidor ha pedido esperar cuando frena. 0 = no lo ha dicho. */
  const [segundos, setSegundos] = useState(0);
  const mirando = useRef(false);

  /**
   * Pregunta quién eres, con reintentos. Sólo un NO del servidor cuenta como estar fuera.
   */
  const mira = useCallback(async () => {
    if (mirando.current) return;
    mirando.current = true;
    setSesion(COMPROBANDO);
    try {
      for (let i = 0; i < INTENTOS.length; i += 1) {
        if (INTENTOS[i]) await espera(INTENTOS[i]);
        try {
          const me = await fetchMe();
          setUser(me);
          setSesion(DENTRO);
          return;
        } catch (e) {
          // QUÉ SIGNIFICA EL FALLO NO SE DECIDE AQUÍ: lo dice `lib/sesion.mjs`, que se prueba
          // sin React. Un «no» del servidor es estar fuera; un silencio, no.
          const dice = loQueDice(e);
          if (dice === FUERA) {
            setUser(null);
            setSesion(FUERA);
            return;
          }
          // UN FRENO NO SE REINTENTA, Y ESTA ES LA LÍNEA QUE FALTABA (6 de octubre de 2026).
          // Un 429 salía por la puerta del silencio, así que esta comprobación lo reintentaba
          // cuatro veces en diez segundos: cuatro peticiones más contra el mismo límite que
          // estaba esperando a vaciarse. Lo único que no puede ayudar cuando el servidor dice
          // «para» es volver a preguntar. Se para, se dice cuánto pide esperar, y vuelve a
          // intentarlo quien está delante.
          if (dice === FRENO) {
            setSegundos(segundosDeFreno(e));
            setSesion(FRENO);
            return;
          }
        }
      }
      setUser(null);
      setSesion(SIN_RESPUESTA);
    } finally {
      mirando.current = false;
      setReady(true);
    }
  }, []);

  useEffect(() => { mira(); }, [mira]);

  const login = async (email, password) => {
    const { user: u } = await loginUser({ email, password });
    setUser(u);
    setSesion(DENTRO);
    return u;
  };

  const register = async (name, email, password, edad = {}) => {
    const { user: u } = await registerUser({ email, name, password, ...edad });
    setUser(u);
    setSesion(DENTRO);
    return u;
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
    setSesion(FUERA);
  };

  // Re-lee el usuario (p. ej. tras volver de Stripe con el plan ya cambiado).
  //
  // CON `useCallback` DESDE EL 4 DE OCTUBRE DE 2026, y no es cosmetica: sin el, esta funcion
  // nace de nuevo en cada pintado, asi que cualquier `useEffect` que la vigile se dispara
  // infinitamente. La alternativa era que cada pantalla que quiera releer al usuario se
  // escribiera un `eslint-disable` encima, o sea repartir la trampa en vez de arreglarla. No
  // depende de nada: `fetchMe` y los dos `set` son estables.
  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setSesion(DENTRO);
      return me;
    } catch {
      // Y AQUÍ TAMPOCO SE ECHA A NADIE: esto se llama con la sesión ya abierta y en mitad de una
      // pantalla. Si no se puede releer, se deja lo que había; el servidor cortará por su cuenta
      // en la siguiente petición si de verdad no hay sesión.
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, ready, sesion, segundos, login, register, logout, refresh, reintenta: mira }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
